import { DEFAULT_GATEWAY_URL } from './utils';

export interface TenantStepData {
  tenantId: string;
  gatewayBaseUrl: string;
  configureQuickBooks: boolean;
  configureShipStation: boolean;
  configureDiagnostic: boolean;
}

export interface StepCardProps {
  children: React.ReactNode;
}

export interface StepFieldProps {
  label: React.ReactNode;
  children: React.ReactNode;
  fullWidth?: boolean;
  wide?: boolean;
  required?: boolean;
}

export interface StepHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
}

export interface StepFormContainerProps {
  children: React.ReactNode;
}

export interface TaskItem {
  id: string;
  title: string;
  endpoint: string;
}

export const STEP_LABELS = ['Tenant', 'Vanilla Settings', 'QuickBooks', 'ShipStation', 'Diagnostic', 'Review & Run'];

export const QBO_TUTORIAL_VIDEO_URL = 'https://youtu.be/OMOJCl2DsUQ?si=rmC2e8NrLeVyWttE';

export const REVIEW_TASKS: TaskItem[] = [
  { id: 'vanilla', title: 'Update ERP Config with vanilla settings', endpoint: 'POST /PxwTenantVanillaSettingSetup' },
  { id: 'qbo-configurator', title: 'Create entries in QBO', endpoint: 'POST /PxwTenantErpConfigQboConfigurator' },
  { id: 'qbo-setup', title: 'Update ERP config with QBO values', endpoint: 'POST /PxwTenantQboSetup' },
  { id: 'shipstation', title: 'Update ERP config with ShipStation values', endpoint: 'POST /PxwTenantShipStationSetup' },
  { id: 'diagnostic', title: 'Update ERP config with Diagnostic Software values', endpoint: 'POST /PxwTenantProcessingSoftwareSetup' },
];

export interface ConnectorConfig {
  apiUser: string;
  baseUrl: string;
  clientId: string;
  apiUserPassword: string;
  userPoolId: string;
}

export interface VanillaSettingsData {
  saas: ConnectorConfig;
  pxw: ConnectorConfig;
}

export const INITIAL_TENANT_DATA: TenantStepData = {
  tenantId: '',
  gatewayBaseUrl: DEFAULT_GATEWAY_URL,
  configureQuickBooks: true,
  configureShipStation: true,
  configureDiagnostic: true,
};

export const INITIAL_CONNECTOR_CONFIG: ConnectorConfig = {
  apiUser: '',
  baseUrl: '',
  clientId: '',
  apiUserPassword: '',
  userPoolId: '',
};

export const INITIAL_VANILLA_SETTINGS: VanillaSettingsData = {
  saas: { ...INITIAL_CONNECTOR_CONFIG },
  pxw: { ...INITIAL_CONNECTOR_CONFIG },
};

export interface QuickBooksData {
  qboClientId: string;
  qboClientSecret: string;
  realmId: string;
  accessToken: string;
  refreshToken: string;
  purchaseOrderCharges: string;
  manuallyConfigureSalesTax: boolean;
}

export const INITIAL_QUICKBOOKS_DATA: QuickBooksData = {
  qboClientId: '',
  qboClientSecret: '',
  realmId: '',
  accessToken: '',
  refreshToken: '',
  purchaseOrderCharges: '',
  manuallyConfigureSalesTax: false,
};

export interface ShipStationData {
  storeNumber: string;
  webhookUrl: string;
  v1ApiKey: string;
  v1SecretKey: string;
  v2ProdKey: string;
}

export const INITIAL_SHIPSTATION_DATA: ShipStationData = {
  storeNumber: '',
  webhookUrl: '',
  v1ApiKey: '',
  v1SecretKey: '',
  v2ProdKey: '',
};

export interface MannapovIceData {
  bearer: string;
  baseUrl: string;
  license: string;
}

export interface PhoneCheckData {
  apiKey: string;
  userName: string;
}

export interface DiagnosticData {
  enableMannapovIce: boolean;
  enablePhoneCheck: boolean;
  mannapovIce: MannapovIceData;
  phoneCheck: PhoneCheckData;
}

export const INITIAL_DIAGNOSTIC_DATA: DiagnosticData = {
  enableMannapovIce: true,
  enablePhoneCheck: true,
  mannapovIce: { bearer: '', baseUrl: '', license: '' },
  phoneCheck: { apiKey: '', userName: '' },
};

export interface PxwConnectorFormData {
  tenant: TenantStepData;
  vanilla: VanillaSettingsData;
  quickBooks: QuickBooksData;
  shipStation: ShipStationData;
  diagnostic: DiagnosticData;
}

export const INITIAL_FORM_DATA: PxwConnectorFormData = {
  tenant: INITIAL_TENANT_DATA,
  vanilla: INITIAL_VANILLA_SETTINGS,
  quickBooks: INITIAL_QUICKBOOKS_DATA,
  shipStation: INITIAL_SHIPSTATION_DATA,
  diagnostic: INITIAL_DIAGNOSTIC_DATA,
};
